/* ============================================
   ADORN MARMOR THEME - Main JS
   Cart drawer, scroll reveal, Shopify cart API
   ============================================ */

(function () {
  'use strict';

  // ============================================
  // SCROLL REVEAL ANIMATIONS
  // ============================================
  const initScrollReveal = () => {
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('in'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
  };

  // ============================================
  // CART DRAWER
  // ============================================
  const CartDrawer = {
    drawer: null,
    overlay: null,

    init() {
      this.drawer = document.getElementById('CartDrawer');
      this.overlay = document.getElementById('CartDrawerOverlay');
      if (!this.drawer) return;

      // Open triggers (cart buttons across the site)
      document.querySelectorAll('[data-cart-trigger]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Close triggers
      this.drawer.querySelectorAll('[data-cart-close]').forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('open')) {
          this.close();
        }
      });

      // Listen for cart updates from add-to-cart forms
      document.addEventListener('cart:added', async (e) => {
        await this.refresh();
        this.open();
      });

      // Quantity update buttons inside drawer
      this.drawer.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-qty-change]');
        if (qtyBtn) {
          e.preventDefault();
          const line = qtyBtn.dataset.line;
          const direction = qtyBtn.dataset.qtyChange;
          this.updateQuantity(line, direction);
        }

        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          this.removeItem(removeBtn.dataset.cartRemove);
        }
      });
    },

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.overlay?.classList.add('open');
      document.body.classList.add('no-scroll');
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.overlay?.classList.remove('open');
      document.body.classList.remove('no-scroll');
    },

    async refresh() {
      try {
        const response = await fetch('/cart?section_id=cart-drawer');
        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const newDrawer = doc.querySelector('.cart-drawer');
        if (newDrawer && this.drawer) {
          this.drawer.innerHTML = newDrawer.innerHTML;
          this.drawer.querySelectorAll('[data-cart-close]').forEach(btn => {
            btn.addEventListener('click', () => this.close());
          });
        }
        this.updateCartCount();
      } catch (e) {
        console.error('Cart refresh failed:', e);
      }
    },

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
          el.dataset.count = cart.item_count;
        });
      } catch (e) {
        console.error('Cart count update failed:', e);
      }
    },

    async updateQuantity(line, direction) {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const item = cart.items[parseInt(line) - 1];
        if (!item) return;
        const newQty = direction === 'increase' ? item.quantity + 1 : Math.max(0, item.quantity - 1);

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line), quantity: newQty })
        });
        await this.refresh();
      } catch (e) {
        console.error('Quantity update failed:', e);
      }
    },

    async removeItem(line) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line), quantity: 0 })
        });
        await this.refresh();
      } catch (e) {
        console.error('Remove item failed:', e);
      }
    }
  };

  // ============================================
  // ADD-TO-CART FORMS (intercept submission)
  // ============================================
  const initAddToCart = () => {

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('form[action*="/cart/add"] [data-cart-trigger]');
      if (!btn || btn.disabled) return;
      if (btn.classList.contains('is-added')) {
        e.preventDefault();
        CartDrawer.open();
      }
    });

    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();

      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn?.innerHTML;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerHTML = 'Adding...';
      }

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
        if (!response.ok) throw new Error('Add to cart failed');
        const item = await response.json();
        document.dispatchEvent(new CustomEvent('cart:added', { detail: item }));
        if (submitBtn) {
          submitBtn.innerHTML = 'Added to cart ✓';
          submitBtn.classList.add('is-added');
        }
        // Cart drawer opens via the cart:added listener above.
        // No forced navigation to /cart — let people keep browsing and add more.
        window.setTimeout(() => {
          if (submitBtn && submitBtn.classList.contains('is-added')) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalText;
            submitBtn.classList.remove('is-added');
          }
        }, 2200);
      } catch (err) {
        console.error('Add to cart error:', err);
        alert('Sorry, something went wrong adding this to your cart.');
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-busy');
          if (submitBtn && !submitBtn.classList.contains('is-added')) {
            submitBtn.innerHTML = originalText;
          }
        }
      }
    });
  };

  // ============================================
  // SEARCH FORM ENHANCEMENT
  // ============================================
  const initSearch = () => {
    const searchInput = document.querySelector('.search-form__input');
    if (!searchInput) return;
    // Could add predictive search here in future
  };

  // ============================================
  // INIT
  // ============================================
  const initCritical = () => {
    // Keep add-to-cart working, but postpone heavier drawer/reveal work.
    initAddToCart();
  };

  const initDeferred = () => {
    CartDrawer.init();
    initSearch();
    // Skip scroll animations on small mobile screens to reduce main-thread work.
    if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) {
      initScrollReveal();
    } else {
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('in'));
    }
  };

  const runDeferred = () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(initDeferred, { timeout: 3000 });
    } else {
      setTimeout(initDeferred, 1800);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCritical, { once: true });
    window.addEventListener('load', runDeferred, { once: true });
  } else {
    initCritical();
    runDeferred();
  }
})();


// Remove Air Reviews floating popup/button if the app injects it after page load.
(function removeAirReviewsWidget(){
  const selectors = [
    '[class*="AirReviews"]', '[id*="AirReviews"]',
    '[class*="air-reviews"]', '[id*="air-reviews"]',
    '[class*="air-review"]', '[id*="air-review"]'
  ];
  const remove = () => {
    selectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => {
        // Do not remove our custom home testimonial section.
        if (!el.closest('.am-review-section') && !el.classList.contains('am-review-section')) {
          el.remove();
        }
      });
    });
    // Remove the specific vertical tab by visible text as fallback.
    document.querySelectorAll('button, a, div').forEach(el => {
      const txt = (el.textContent || '').trim().toLowerCase();
      if (txt === 'our reviews' && !el.closest('.am-review-section')) {
        const box = el.closest('button, a, [role="button"], div');
        if (box) box.remove();
      }
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', remove, { once: true });
  } else {
    remove();
  }
  window.addEventListener('load', remove, { once: true });
  setTimeout(remove, 1500);
  setTimeout(remove, 3500);
})();
